<template>
	<b-overlay no-center variant="white" :show="isOverlayDisplayed" rounded="sm" no-fade :opacity="overlayOpacity"
		:class="isOverlayDisplayed ? 'overflow-hidden' : 'overflow-auto'" class="w-100 h-100" :style="generateVariantStyle(Variant)">
		<b-card no-body class="d-flex flex-column mh-100 h-100 w-100 rounded-0">
			<!-- SI TOUTE LA LARGEUR (isFullWidth) : Liste des boutons d'accès aux formulaires sur toute la largeur -->
			<b-tabs card content-class="flex-grow-1 overflow-auto bg-white" :active-nav-item-class="'text-' + Variant" nav-class="p-0"
				:fill="($screen.breakpoint === 'xs' || $screen.breakpoint === 'sm')"
				class="d-flex flex-column mh-100 h-100">
				<template #tabs-end>
					<b-row class="flex-grow-1 justify-content-end mr-0">
						<!-- Bouton d'annulation de création d'un nouvel objet-->
						<b-col v-if="consultMode === objectDisplayingModeEnum.New" cols="auto" align-self="center" class="px-2">
							<b-button @click="navigationMediator.CancelNew(parentNode)" variant="outline-black" v-b-tooltip.hover :title="$t('Cancel')" class="btn-circle-small p-0">
								<!-- <b-icon icon="ban" /> -->
								<i class="bi-ban" />
							</b-button>
						</b-col>

						<!-- Si l'utilisateur courant a des droits sur la fiche objet -->
						<template v-if="masterAction('ObjectSheet')">
							<!-- Bouton de sauvegarde de la fiche objet -->
							<b-col v-if="consultMode !== objectDisplayingModeEnum.Read && !isSaveHidden" cols="auto" align-self="center" class="px-1">
								<!-- WarnInErrorFields : Certains champs comportent des erreurs -->
								<b-icon v-if="formsErrors && Object.keys(formsErrors).length > 0" @click="save()" v-b-tooltip.noninteractive.hover :title="$t('WarnInErrorFields')" icon="exclamation-circle-fill" variant="danger" class="position-absolute top-0 end-0 translate-middle rounded-pill mt-0" style="z-index:1 !important" role="button" />
								<!-- WarnUpdatedFields : Des champs ont été modifiés -->
								<b-icon v-else-if="updatedForms && updatedForms.length > 0" @click="save()" v-b-tooltip.noninteractive.hover :title="$t('WarnUpdatedFields')" icon="exclamation-circle-fill" variant="info" class="position-absolute top-0 end-0 translate-middle rounded-pill mt-0" style="z-index:1 !important" role="button" />
								<b-button @click="save()" variant="outline-dark" v-b-tooltip.noninteractive.hover :title="$t('Save')" :disabled="saveMode !== saveModeEnum.NewObject && (!updatedForms || updatedForms.length == 0)" :class="['btn-circle-small p-0']">
									<!-- <font-awesome-icon icon="save" /> -->
									<!-- @hack L'icone floppy inclus dans les versions récentes de Bootstrap-Icons n'est pas dans Bootstrap-vue (https://bootstrap-vue.org/docs/icons)
										Il n'est donc pas possible d'utiliser la balise b-icon ici -->
									<i :class="'bi-floppy'" />
								</b-button>
							</b-col>

							<!-- Historique des version de l'objet -->
							<b-col v-if="!isVersionsHidden"  cols="auto" align-self="center" class="px-1">
								<!-- Records : Historique -->
								<b-dropdown v-if="consultMode !== objectDisplayingModeEnum.New" id="dropdownVersions" v-b-tooltip.noninteractive.hover :title="$t('Records')" size="sm" ref="dropdownVersions" variant="outline-black" :text="$t('Records')" 
									toggle-class="rounded-pill"
									menu-class="p-0">
									<template #button-content>
										<b-icon icon="clock-history" /> {{ versionDate }}
									</template>
									<b-dropdown-form form-class="p-0 m-0">
										<!-- HACK: style avec font-family et font-size appliqué sur VersionSelector car la b-dropdown applique un style qui casse les fonts par défaut mis dans le App.vue -->
										<VersionSelector :Forms.sync="forms"
														@UpdateForm="updateForm()"
														@DisplayLoadingOverlay="displayOverlay(overlayEnum.Loading, 0.3)"
														@HideLoadingOverlay="hideOverlay()"
														@HideVersionSelector="hideVersionSelector()" />
									</b-dropdown-form>
								</b-dropdown>
							</b-col>
							<b-col cols="auto" align-self="center" class="pl-1 pr-0"></b-col>
						</template>
					</b-row>
				</template>
			
				<!-- Liste des onglets de la fiche objet -->
				<b-tab v-if="checkRightForm(form.Name)" v-for="(form, index) in getFormsButtons()" :key="index" :lazy="form.LazyLoading == true || !(loadedForms.includes(form.Name))"
					@click="setComponentTarget(form.Name)"
					:active="formName === form.Name"
					:disabled="form.MenuButtonState === menuButtonStateEnum.Disabled"
					title-link-class="" class="h-100">
					<template #title>
						<div class="d-inline-block inline-block position-relative bg-transparent border-0">{{ form.Label }}
							<!-- ErrorForm : Ce formulaire comporte des erreurs -->
							<b-icon v-if="formsErrors && form.Name in formsErrors" v-b-tooltip.noninteractive.hover :title="$t('ErrorForm')" icon="exclamation-circle-fill" class="text-danger" role="button" />
							<!-- InfoUpdatedForm : Des champs ont été modifiés dans ce formulaire -->
							<b-icon v-else-if="updatedForms && updatedForms.includes(form.Name)" v-b-tooltip.noninteractive.hover :title="$t('InfoUpdatedForm')" icon="exclamation-circle-fill" class="text-info" role="button" />
							<!-- Beta : Beta -->
							<b-badge v-if="form.MenuButtonState === menuButtonStateEnum.Disabled" :variant="Variant" class="position-absolute top-0 start-100 translate-middle rounded-pill mt-2">
								{{ $t('Beta') }}
							</b-badge>
						</div>
					</template>

					<b-row class="flex-grow-1 overflow-auto h-100">
						<!-- Affichage des composants associés au formulaire selon le bouton cliqué -->
						<b-col sm="12" md="12" lg="12" :xl="form.IsFullWidth ? 12 : 8" order="3" :order-xl="form.IsFullWidth ? 3 : 1" class="p-0">
							<!-- targetKey est utilisé pour forcer le composant à se réinitialiser lors du save() -->
							<component v-if="form.Target && form.Target !== ''" v-bind:is="form.Target" v-bind:ConsultMode="consultMode"
								:Form.sync="form"
								:Variant="Variant"
								:NodeId="nodeId"
								:ObjectType="objectType"
								:Action="getActionForm(form.Name)" />
						</b-col>
					</b-row>
				</b-tab>
			</b-tabs>
		</b-card>

		<!-- Overlay pendant chargement de données -->
		<template #overlay>
			<!-- Template affiché par le <b-overlay> au-dessus lors du chargement d'un objet ou d'une version -->
			<Welcome v-if="displayedOverlay === overlayEnum.Loading" :DisplayBrand="false" :Loading="false" :Variant="Variant" class="text-center">
			</Welcome>

			<!-- Template affiché par le <b-overlay> au-dessus lors de la sauvegarde d'un objet ou d'une version -->
			<Welcome v-if="displayedOverlay === overlayEnum.Saving" :DisplayBrand="false" :Loading="false" :Variant="Variant" class="text-center">
				<template v-slot:MiscContent>
					<div class="b-skeleton w-100 b-skeleton-icon b-skeleton-animate-fade bg-transparent text-gray-middle-dark pt-4" align-v="center">
						<b-icon icon="file-richtext-fill" font-scale="7"/>
					</div>
					<!-- ObjectFormSavedInProgress : Sauvegarde de la fiche objet en cours... -->
					<h4 class="pt-4">{{ $t('ObjectFormSavedInProgress') }}</h4>
				</template>
			</Welcome>
		</template>

		<!-- Zone d'affichage des erreurs de validation de formulaire -->
		<b-toast id="toastValidation" variant="danger" solid no-auto-hide toaster="b-toaster-top-center">
			<template #toast-title>
				<b-icon icon="exclamation-circle" class="h4 mt-2 mr-2" />
				<!-- Errors : Erreurs -->
				<b>{{ $t('Errors') }}</b>
			</template>
			<b-list-group>
				<template v-for="(form, formName, formIndex) in formsErrors">
					<b-list-group-item v-for="(error, fieldName, fieldIndex) in formsErrors[formName]?.FieldsErrors" v-bind:key="fieldIndex" 
						v-on:click="setComponentTarget(formName, form.Target, form.FormLabel, form.IsFullWidth)" variant="danger" role="button">
						<!-- Form : Formulaire -->
						<b-icon icon="arrow-right-circle" class="mr-1" />{{ $t('Form') }} '{{ form.FormLabel }}' : {{ error }}
					</b-list-group-item>
				</template>
			</b-list-group>
		</b-toast>
	</b-overlay>
</template>

<script lang="ts">
	import { Prop, Watch, Vue, Inject } from "vue-property-decorator";
	import { Getter } from 'vuex-class';
	import Component, { mixins } from 'vue-class-component';
	import Dictionary from '@/types/Dictionary';
	import locales from "./locales/ObjectDisplaying.json";

	import Welcome from "@/components/commons/Welcome.vue";

	/** Champs génériques */
	import Form from "./ObjectDisplaying/Form.vue";
	import FormDraft from "@/types/fields/FormDraft";
	import Fields from "@/components/commons/Fields.vue";

	/** Fonctions utilitaires de manipulation des objets */
	import { ObjectUtils, OverlayUtils, ThemeUtils } from "@/components/commons/Utils";

	/** Sélecteur de version de fiche objet */
	import VersionSelector from "./ObjectDisplaying/VersionSelector.vue"

	/** Enumérations */
	import SaveModeEnum from "@/enums/SaveModeEnum";
	import ObjectDisplayingModeEnum from "@/enums/ObjectDisplayingModeEnum";
	import OverlayEnum from "@/enums/OverlayEnum";
	import MenuButtonStateEnum from "@/enums/fields/MenuButtonStateEnum";

	/** Formulaires de fiche objet */
	import ObjectDashboard from "./ObjectDisplaying/ObjectDashboard.vue";
	import Children from "./ObjectDisplaying/Children.vue";
	import Identity from "./ObjectDisplaying/Identity.vue";
	import Events from "./ObjectDisplaying/Events.vue";
	import Tariffs from "./ObjectDisplaying/Tariffs.vue";
	import Factors from "./ObjectDisplaying/Factors.vue";
	import Pictures from "./ObjectDisplaying/Pictures.vue";
    import Allocation from "./ObjectDisplaying/Allocation.vue";
	import Contacts from "./ObjectDisplaying/Contacts.vue";
	import Localization from "./ObjectDisplaying/Localization.vue";
	import TreeNode from "@/types/TreeNode";
	/** Médiateur de communication inter-composants/stores VueX dédié à la navigation */
	import { NavigationMediator } from "@/mediators/NavigationMediator";
	import IFeature from "@/interfaces/IFeature";

	/** Composant de chargement de la fiche objet avec chacun de ses formulaires */
	@Component({
		components: {
			Welcome,

			/** Génération de formulaires génériques */
			Form,
			Fields,

			/** Sélecteur de versions de fiche objet */
			VersionSelector,

			/** Formulaires de fiche objet */
			ObjectDashboard,
			Children,
			Identity,
			Events,
			Tariffs,
            Factors,
			Pictures,
			Allocation,
			Contacts,
			Localization
		},
		i18n: {	messages: locales }
	})
	export default class ObjectDisplaying extends mixins(ObjectUtils, OverlayUtils, ThemeUtils) implements IFeature {
		/** Variant de couleurs à appliquer */
        @Prop()
        public Variant!:string;

		/**
         * Liste des Id de MainDashboard chargés dans un onglet 
         * @description Pour tracker les onglets de dashboards qui ont déjà été ouvert et changer la prop Lazy à false si déjà ouvert ==> Evite que l'onglet se recharge avec tous les LoadResults de widget à chaque réouverture */
        private loadedForms: string[] = [];

		//#region Gestion masque (overlay) affiché pendant le chargement
		/** Pour permettre l'utilisation des enums et objet screen dans la section template */
		private overlayEnum = OverlayEnum;
		private objectDisplayingModeEnum = ObjectDisplayingModeEnum;
		private menuButtonStateEnum = MenuButtonStateEnum;
		private saveModeEnum = SaveModeEnum;
		//#endregion Gestion masque (overlay) affiché pendant le chargement

		@Getter('ObjectStore/GetNodeId')
		private nodeId!: string;

		@Getter('ObjectStore/GetObjectType')
		private objectType!: string;

		@Getter('ObjectStore/GetParentNode')
		private parentNode!: TreeNode;

		/** Obtient la version sélectionnée */
		@Getter('ObjectStore/GetVersionId')
		private versionId!: number;

		/** Indique si le bouton de sauvegarde est masqué ou non */
		@Getter('ObjectStore/GetIsSaveHidden')
		private isSaveHidden!: boolean;

		/** Indique si l'historique des versions doit être affiché ou non pour l'objet */
		@Getter('ObjectStore/GetIsVersionsHidden')
		private isVersionsHidden!: boolean;

		/** Obtient la date formatée de la version sélectionnée */
		@Getter('ObjectStore/GetFormatedVersionDate')
		private versionDate!: string;

		//#region Gestion composant de formulaire dynamique
		/**  Liste des formulaires */
		private forms: FormDraft[] = [];


		/** Indique si des formulaires de la fiche objet comporte des champs mis à jour */
		@Getter('ObjectStore/GetUpdatedForms')
		private updatedForms!: string[];

		/** Liste des formulaires à faire apparaitre dans le menu  */
		private getFormsButtons(): FormDraft[] {
			console.debug("ObjectDisplaying - getFormsButtons() - forms : ", this.forms);

			return this.forms.filter(x => x.MenuButtonState === MenuButtonStateEnum.Visible || x.MenuButtonState === MenuButtonStateEnum.Disabled);
		}

		/** Nom du formulaire qui doit être affiché */
		private formName: string = "ObjectDashboard";

		/** Obtient et défini le formulaire de fiche objet par défaut */
		private loadDefaultForm() {
			/** Pour forcer le rechargement du formulaire */
			Vue.set(this, 'formName', JSON.parse(JSON.stringify(this.$store.getters['ObjectStore/GetDefaultFormName'])));

			this.loadedForms.push(this.formName);
			console.debug("ObjectDisplaying - loadDefaultForm() - this.forms.length : ", this.forms.length);
		}

		/**
		 * Affecte le composant cible et le libellé du formulaire à charger
		 * @param formName		Nom du formulaire à charger
		 */
		private setComponentTarget(formName: string) {
			this.formName = formName;

			/** Met à jour la liste des formulaire chargés pour lazy */
            if(!this.loadedForms.includes(formName))
                this.loadedForms.push(formName)
			console.debug("ObjectDisplaying -  setComponentTarget : %O", this.loadedForms)
		}
		//#endregion Gestion composant de formulaire dynamique

		//#region Gestion affichage des erreurs
		/** Erreurs présentes sur des formulaires */
		@Getter('ObjectStore/GetFormsErrors')
		public formsErrors!: Dictionary<{ Target: string, FormLabel: string, IsFullWidth: boolean, FieldsErrors: Dictionary<string> }>;
		//#endregion Gestion affichage des erreurs

		//#region Gestion mode affichage/enregistrement
		/** Obtient les droits associés à une fonction */
		@Getter('GetMasterAction')
        private masterAction!: (featureName: string) => string | undefined;

		/**
		 * Vérifie les droits pour l'éléments du formulaire que l'on souhaite afficher
		 * @param formName nom du formulaire
		 */
		private checkRightForm(formName: string){
			let action: string | undefined = undefined;
			if(formName.startsWith("ObjectDashboard")){
				action = this.masterAction('ObjectDashboard')
				console.debug("ObjectDisplaying - showObjectSheetItem - action ObjectDashboard %O", action)
			}else{
				action = this.masterAction('ObjectSheet')
				console.debug("ObjectDisplaying - showObjectSheetItem - action ObjectSheet %O", action)
			}
			return action;
		}
		/**
		 * Récupère l'action du formulaire via son nom
		 * @param formName nom du formulaire
		 */
		private getActionForm(formName: string){
			let action: string | undefined = undefined;
			if(formName.startsWith("ObjectDashboard")){
				action = this.$store.getters['GetMasterAction']('ObjectDashboard');
				console.debug("ObjectDisplaying - getActionForm - action ObjectDashboard %O", action)
			}else{
				action = this.$store.getters['GetMasterAction']('ObjectSheet');
				console.debug("ObjectDisplaying - getActionForm - action ObjectSheet %O", action)
			}
			return action;
		}
		
		/** Indique si le fomulaire est en mode edition (les champs peuvent être modifiés et sauvegardés), en consultation seulement ou s'il s'agit d'un nouvel objet (pas créé dans le back) */
		private consultMode: ObjectDisplayingModeEnum = ObjectDisplayingModeEnum.Read;

		@Getter('ObjectStore/GetSaveMode')
		private saveMode!: SaveModeEnum;

		/** Détection du changement du mode de sauvegarde la fiche objet */
		@Watch('saveMode')
		private saveModeChanged() {
			console.debug("ObjectDisplaying - saveModeChanged - this.saveMode : " + this.saveMode);

			// Masque l'Overlay de sélection de noeud dans le composant parent si en mode création d'un nouvel objet
			if (this.saveMode === SaveModeEnum.NewObject){
				console.debug("ObjectDisplaying - saveModeChanged - this.saveMode === SaveModeEnum.NewObject - this.saveMode : " + this.saveMode);
				this.$emit('HideOverlayEvent');
				this.nodeUpdated();
			}
			else {
				console.debug("ObjectDisplaying - saveModeChanged - this.saveMode !== SaveModeEnum.NewObject - this.saveMode : " + this.saveMode);
				this.consultMode = (this.$store.getters['CheckAction']('Write', 'ObjectSheet') ? ObjectDisplayingModeEnum.Write : ObjectDisplayingModeEnum.Read);
			}
		}
		//#endregion Gestion mode affichage/enregistrement

		@Getter('ObjectStore/GetRefreshKey')
		private reload!: number;

		@Watch('reload')
		private async reloadAll() {
			console.debug("ObjectDisplaying - reloadAll");
			await this.loadObject(true);
		}

		/** 
		 * Met à jour isOverlayDisplayed selon qu'un noeud d'objet soit sélectionné ou non dans l'arborescence 
		 * @description immediate: true dans le décorateur permet de forcer l'évaluation de ce watcher dès chargement du composant 
		 */
		private async nodeUpdated() {
			if (this.saveMode === SaveModeEnum.NewObject) {
				Vue.set(this, 'consultMode', ObjectDisplayingModeEnum.New);
				this.hideOverlay();

				/** Charge la liste des formulaires pour le nouveau type d'objet */
				Vue.set(this, 'forms', this.$store.getters['ObjectStore/GetForms']);
				Vue.nextTick(() => {
					Vue.nextTick(() => {
						this.loadDefaultForm();
					});
				});
			}
			else {
				if (this.nodeId)
					await this.loadObject(true);
				else	// Affiche un masque au-dessus du formulaire tant qu'un id de noeud n'est pas sélectionné
					this.displayOverlay(OverlayEnum.SingleNodeSelection, 1);
			}
		}

		/**
		 * Chargement d'un objet
		 * @param loadCurrentVersion : Indique si la version en cours (dernière version) doit être chargée ou non
		 */
		private async loadObject(loadCurrentVersion = false) {
			// Masque le toaster affichant les erreurs de validation
			this.$bvToast.hide('toastValidation');

			console.debug("ObjectDisplaying - loadObject() - loadCurrentVersion : ", loadCurrentVersion);

			if (this.masterAction('ObjectSheet') || this.masterAction('ObjectDashboard')) {
				let currentVersionId = this.$store.getters['ObjectStore/GetCurrentVersionId'];
				if ((currentVersionId && currentVersionId == this.versionId) || loadCurrentVersion) {	// S'il s'agit de la version courante (dernière version)
					 this.displayOverlay(OverlayEnum.Loading, 1);
					this.$store.dispatch("ShowLoadingMask", this.$t('ObjectFormLoadingInProgress'));

					// Chargement de la liste des formulaires pour l'objet courant
					await this.$store.dispatch("ObjectStore/LoadData").then(async () => {
						console.debug("ObjectDisplaying - loadObject - this.isOverlayDisplayed = false;");
						
						this.forms = this.$store.getters['ObjectStore/GetForms'];

						// Lancement chargement du tableau de bord objet ici uniquement après chargement de la liste des formulaires de fiche objet pour basculer sur le formulaire par défaut si aucun tableau de bord objet n'existe
						console.debug("ObjectDisplaying - loadObject() - this.$refs", this.$refs);
						
						this.loadDefaultForm();
						// this.setDefaultForm();

						this.hideOverlay();
					})
					.catch(err => {
						console.error(err);
						this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // Une erreur s'est produite.
						title: this.$t('Error').toString(), // Erreur
							autoHideDelay: 10000,
							variant: "danger",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					})
					.finally(() => {
						this.$store.dispatch("HideLoadingMask", {});
					});
				}
				else {
					// Chargement de la liste des formulaires pour l'objet courant et la version sélectionnée
					this.displayOverlay(OverlayEnum.Loading, 0.3);
					this.$store.dispatch("ShowLoadingMask", this.$t('ObjectFormLoadingInProgress'));

					await this.$store.dispatch("ObjectStore/LoadVersion", { versionId: this.versionId }).then(response => {
						this.hideOverlay();
					})
					.finally(() => {
						this.$store.dispatch("HideLoadingMask", {});
					});
				}
			}
		}

		/** Sauvegarde des données de l'objet et passage du formulaire en mode consultation (ObjectDisplayingModeEnum.Read) */
		private save() {
			this.displayOverlay(OverlayEnum.Saving, 0.3);
			this.$bvToast.hide('toastValidation');

			this.$store.dispatch("ObjectStore/Save", { forms: this.forms }).then(response => {
				/** @todo: OPTIMISATION : voir pour ne rafraichir que les tableaux et images nécessitant de récupérer les Ids côté back */
				console.debug("BaseObjectForm - addUpdatedForm() - ObjectDisplaying --> save");

				// Rechargement de la liste des Fields
				this.forms = this.$store.getters['ObjectStore/GetForms'];

				/** Efface la liste des formulaire modifiés @todo voir pour le faire qu'une seule fois lors du save dans ObjectStore */
				Vue.nextTick(() => {
					Vue.nextTick(() => {
						this.$store.dispatch("ObjectStore/CleanUpdatedForm");
					});
				});

				
				this.$bvToast.toast(this.$t('MsgSaveSuccess').toString(), { // L'enregistrement a été réalisé avec succès.
					title: this.$t('Information').toString(),
					autoHideDelay: 5000,
					variant: "success",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			})
			.catch((error) => {
				console.error(error);
				this.$bvToast.show('toastValidation');
			})
			.finally(() => {
				// Masque l'overlay
				this.hideOverlay();
			});
		}

		/** Ferme la dropdown de sélection de versions */
		private hideVersionSelector() {
			(this.$refs.dropdownVersions as any).hide(true);
		}

		mounted() {
			console.debug('ObjectDisplaying - mounted()');
			/** Met la fiche objet en lecture seule ou lecture/écriture selon si l'utilisateur courant a les droits ou non */
			if (this.masterAction('ObjectSheet'))
				this.consultMode = (this.$store.getters['CheckAction']('Write', 'ObjectSheet') ? ObjectDisplayingModeEnum.Write : ObjectDisplayingModeEnum.Read);

			this.saveModeChanged();
		}

		public async SetNode(node: TreeNode): Promise<any> {
			return new Promise(async (resolve, reject) => {
				await this.$store.dispatch("ObjectStore/SetNode", { node: node });
				await this.nodeUpdated();
				resolve('SingleNode affecté !');
			});
		}

		beforeDestroy() {
			/** On réinitialise le store VueX de la fiche objet à la sortie */
			this.$store.dispatch("ObjectStore/Reset", {});
		}

		/** Médiateur de communication inter-composants/stores VueX dédié à la navigation */
		@Inject()
		private navigationMediator: NavigationMediator;
	}
</script>

<style scoped>

</style>
